import { toast } from "sonner";
import { getOriginalFetch } from "./fetchInterceptor";

// ─── Configurable API Base URL ──────────────────────────
// Priority: localStorage custom URL > env var > relative "/api"
function getApiBase(): string {
  const stored = localStorage.getItem("rb_api_url");
  if (stored) return stored;
  const env = import.meta.env.VITE_API_URL;
  if (env) return env;
  return "api";
}

// Let user override the API URL at runtime (e.g. for cross-domain hosting)
export function setApiBase(url: string) {
  localStorage.setItem("rb_api_url", url);
}
export { getApiBase };
export function clearApiBase() {
  localStorage.removeItem("rb_api_url");
}

// Dynamic API base — evaluated every time to support runtime configuration
function apiBase(): string { return getApiBase(); }
let phpBackendAvailable = false;
let phpBackendChecked = false;
let phpBackendCheckTime = 0;
const BACKEND_CHECK_TTL_MS = 30000; // Recheck every 30 seconds

export interface BackendCheckResult {
  available: boolean;
  pingOk: boolean;
  trpcOk: boolean;
  pingError?: string;
  trpcError?: string;
  trpcStatus?: string;
}

export function setBackendAvailable(val: boolean) {
  phpBackendAvailable = val;
}

export function hasPhpBackend(): boolean {
  // If user has a valid auth token, assume backend is reachable
  // (they must have logged in successfully at some point)
  if (phpBackendAvailable) return true;
  if (!!localStorage.getItem("rb_api_token")) return true;
  return false;
}

export function resetBackendCheck() {
  phpBackendChecked = false;
  phpBackendCheckTime = 0;
}

export function hasAuthToken(): boolean {
  return !!localStorage.getItem("rb_api_token");
}

export function isBackendChecked(): boolean {
  return phpBackendChecked;
}

export function markBackendUnavailable() {
  phpBackendAvailable = false;
}

// ─── Token Helper ───────────────────────────────────────
function getToken(): string | null {
  return localStorage.getItem("rb_api_token");
}

/**
 * Build fetch headers with Authorization Bearer token.
 * All API calls MUST use this to ensure the token is sent via header.
 */
function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = getToken();
  const headers: Record<string, string> = { ...extra };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Debug log to console with [RB-AUTH] prefix for easy filtering.
 */
function authDebug(label: string, data: unknown) {
  if (import.meta.env.DEV) {
    console.log(`[RB-AUTH] ${label}:`, data);
  }
}

export async function checkPhpBackend(): Promise<BackendCheckResult> {
  const now = Date.now();
  if (phpBackendChecked && (now - phpBackendCheckTime) < BACKEND_CHECK_TTL_MS) {
    return { available: phpBackendAvailable, pingOk: phpBackendAvailable, trpcOk: phpBackendAvailable };
  }
  phpBackendChecked = true;
  phpBackendCheckTime = now;

  const result: BackendCheckResult = { available: false, pingOk: false, trpcOk: false };
  const base = apiBase();

  // Use original fetch to bypass the interceptor for diagnostics
  const rawFetch = getOriginalFetch();

  // Step 1: Ping the simplest PHP endpoint (no DB, no auth)
  try {
    const pingUrl = base + "/ping.php";
    console.log("[checkPhpBackend] Pinging:", pingUrl);
    const pingRes = await rawFetch(pingUrl, { method: "GET", cache: "no-store" });
    if (pingRes.status !== 200) {
      result.pingError = `HTTP ${pingRes.status} from ${pingUrl}`;
      console.log("[checkPhpBackend] Ping failed:", result.pingError);
      phpBackendAvailable = false;
      return result;
    }
    const pingData = await pingRes.json().catch(() => null);
    if (!pingData?.pong) {
      result.pingError = "Invalid ping response from " + pingUrl;
      console.log("[checkPhpBackend] Invalid ping response");
      phpBackendAvailable = false;
      return result;
    }
    result.pingOk = true;
    console.log("[checkPhpBackend] Ping OK — PHP version:", pingData.php || "unknown");
  } catch (err) {
    result.pingError = err instanceof Error ? err.message : "Network error";
    console.log("[checkPhpBackend] Ping error:", result.pingError);
    phpBackendAvailable = false;
    return result;
  }

  // Step 2: Test that auth endpoint is reachable (we expect 400/401, not HTML)
  try {
    const authUrl = base + "/auth/login.php";
    console.log("[checkPhpBackend] Testing auth endpoint:", authUrl);
    const authRes = await rawFetch(authUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}), // empty body → should return 400 "Username and password required"
      cache: "no-store",
    });
    const authText = await authRes.clone().text();
    console.log("[checkPhpBackend] Auth endpoint status:", authRes.status, "body preview:", authText.substring(0, 100));
    if (authRes.status === 400 && authText.includes("Username and password required")) {
      result.trpcOk = true;
      console.log("[checkPhpBackend] Auth endpoint OK — PHP is executing correctly.");
    } else if (authRes.status === 200 && authText.trimStart().startsWith("<")) {
      result.trpcOk = false;
      result.trpcError = "Auth endpoint returned HTML (was rewritten to index.html). Check .htaccess / server config.";
      console.warn("[checkPhpBackend] Auth endpoint returned HTML — server is rewriting API requests!");
    } else {
      result.trpcOk = true; // Some response is fine — PHP is running
      console.log("[checkPhpBackend] Auth endpoint responded with status", authRes.status);
    }
  } catch (err) {
    result.trpcOk = false;
    result.trpcError = err instanceof Error ? err.message : "Network error";
    console.warn("[checkPhpBackend] Auth endpoint error:", result.trpcError);
  }

  phpBackendAvailable = result.pingOk && result.trpcOk;
  result.available = phpBackendAvailable;
  return result;
}

export async function apiLogin(username: string, password: string) {
  const res = await fetch(`${apiBase()}/auth/login.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || data.error || `Server error: ${res.status}`);
  }
  const data = await res.json();
  if (data.error && !data.token) {
    throw new Error(data.error);
  }
  return data;
}

export async function apiRegister(username: string, password: string, name: string) {
  const res = await fetch(`${apiBase()}/auth/register.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ username, password, name }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || data.error || `Server error: ${res.status}`);
  }
  const data = await res.json();
  if (data.error && !data.token) {
    throw new Error(data.error);
  }
  return data;
}

export async function apiMe() {
  const res = await fetch(`${apiBase()}/auth/me.php`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || "Session expired");
  }
  return res.json();
}

export async function apiLogout() {
  await fetch(`${apiBase()}/auth/logout.php`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
  }).catch(() => {});
  localStorage.removeItem("rb_api_token");
}

// ─── Password Management ────────────────────────────────
export async function apiChangePassword(currentPassword: string, newPassword: string) {
  const token = getToken();
  const body = token
    ? { action: 'change', current_password: currentPassword, new_password: newPassword, __token: token }
    : { action: 'change', current_password: currentPassword, new_password: newPassword };
  const url = token
    ? `${apiBase()}/auth/password.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/auth/password.php`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function apiResetUserPassword(userId: number, newPassword: string) {
  const token = getToken();
  const body = token
    ? { action: 'reset', user_id: userId, new_password: newPassword, __token: token }
    : { action: 'reset', user_id: userId, new_password: newPassword };
  const url = token
    ? `${apiBase()}/auth/password.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/auth/password.php`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function apiListUsers() {
  const res = await fetch(`${apiBase()}/users/list.php`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function apiCreateUser(payload: {
  username: string;
  password: string;
  name?: string;
  email?: string;
  role?: string;
}) {
  const res = await fetch(`${apiBase()}/users/create.php`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function apiDeleteUser(userId: number) {
  const res = await fetch(`${apiBase()}/users/delete.php?id=${userId}`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ─── Assessment ─────────────────────────────────────────
export async function apiSaveAssessment(payload: {
  sampleName: string;
  params: Record<string, unknown>;
  blended?: Record<string, unknown>;
  limits?: Record<string, unknown>;
  result?: Record<string, unknown>;
  dosingConfig?: Record<string, unknown>[];
  equipmentValidation?: Record<string, unknown>[];
  simulation?: Record<string, unknown>;
  customerId?: number;
  standard?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  country?: string;
}) {
  const token = getToken();
  authDebug("apiSaveAssessment token", token ? `${token.substring(0, 20)}...` : "NONE");

  if (!token) {
    authDebug("apiSaveAssessment", "No token in localStorage — save will fail auth");
  }

  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/assessment/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/assessment/save.php`;

  authDebug("apiSaveAssessment URL", url.substring(0, 80) + "...");

  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });

  const text = await res.text();
  authDebug("apiSaveAssessment response status", res.status);

  if (res.status !== 200) {
    let msg = `HTTP ${res.status}`;
    try { const j = JSON.parse(text); if (j.message || j.error) msg = j.message || j.error; } catch {}
    throw new Error(msg);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Invalid JSON from server: " + text.substring(0, 200));
  }
}

export async function apiMyAssessments() {
  const res = await fetch(`${apiBase()}/assessment/list.php`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load assessments");
  const data = await res.json();
  // PHP returns {success: true, results: [...]} — extract the array
  return Array.isArray(data) ? data : (data.results || []);
}

// ─── Customers ──────────────────────────────────────────
export async function apiSaveCustomer(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/customers/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/customers/save.php`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to save customer");
  return res.json();
}

export async function apiListCustomers() {
  const res = await fetch(`${apiBase()}/customers/list.php`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load customers");
  const data = await res.json();
  // PHP returns {success: true, customers: [...]} — extract the array
  return Array.isArray(data) ? data : (data.customers || []);
}

export async function apiUpdateCustomer(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/customers/update.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/customers/update.php`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to update customer");
  return res.json();
}

export async function apiDeleteCustomer(id: number) {
  const token = getToken();
  const url = token
    ? `${apiBase()}/customers/delete.php?id=${id}&_token=${encodeURIComponent(token)}`
    : `${apiBase()}/customers/delete.php?id=${id}`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete customer");
  return res.json();
}

// ─── Samples ────────────────────────────────────────────
export async function apiSaveSample(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/samples/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/samples/save.php`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to save sample");
  return res.json();
}

export async function apiListSamples(customerId?: number) {
  const qs = customerId ? `?customer_id=${customerId}` : '';
  const res = await fetch(`${apiBase()}/samples/list.php${qs}`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load samples");
  const data = await res.json();
  // PHP returns {success: true, samples: [...]} — extract the array
  return Array.isArray(data) ? data : (data.samples || []);
}

export async function apiListInfluentSamples(customerId?: number, options?: { type?: 'influent' | 'effluent'; adminAll?: boolean }) {
  const params = new URLSearchParams();
  if (customerId) params.set('customer_id', String(customerId));
  if (options?.type === 'effluent') params.set('type', 'effluent');
  if (options?.adminAll) params.set('admin_all', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${apiBase()}/influent-samples/list.php${qs}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load influent samples');
  const data = await res.json();
  return Array.isArray(data) ? data : (data.samples || []);
}

export async function apiSaveInfluentSample(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/influent-samples/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/influent-samples/save.php`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to save influent sample');
  return res.json();
}

export async function apiUpdateSample(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/samples/update.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/samples/update.php`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to update sample");
  return res.json();
}

export async function apiDeleteSample(id: number) {
  const token = getToken();
  const url = token
    ? `${apiBase()}/samples/delete.php?id=${id}&_token=${encodeURIComponent(token)}`
    : `${apiBase()}/samples/delete.php?id=${id}`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete sample");
  return res.json();
}

// ─── Reports ────────────────────────────────────────────
export async function apiSaveReport(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/reports/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/reports/save.php`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = JSON.parse(text); if (j.error || j.message) msg = j.error || j.message; } catch {}
    throw new Error(msg);
  }
  let data: any;
  try { data = JSON.parse(text); } catch {
    throw new Error("Invalid JSON from server: " + text.substring(0, 200));
  }
  // PHP may return HTTP 200 with {success: false, error: "..."}
  if (data && data.success === false) {
    throw new Error(data.error || data.message || "Server reported failure");
  }
  return data;
}

export async function apiListReports() {
  const res = await fetch(`${apiBase()}/reports/list.php`, {
    headers: authHeaders(),
    credentials: "include",
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = JSON.parse(text); if (j.error || j.message) msg = j.error || j.message; } catch {}
    throw new Error(msg);
  }
  let data: any;
  try { data = JSON.parse(text); } catch {
    throw new Error("Invalid JSON from reports/list.php: " + text.substring(0, 200));
  }
  // PHP returns {success: true, reports: [...]} — extract the array
  return Array.isArray(data) ? data : (data.reports || []);
}

export async function apiDeleteReport(id: number) {
  const token = getToken();
  const url = token
    ? `${apiBase()}/reports/delete.php?id=${id}&_token=${encodeURIComponent(token)}`
    : `${apiBase()}/reports/delete.php?id=${id}`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete report");
  return res.json();
}

// ─── Proposals ──────────────────────────────────────────────
export async function apiSaveProposal(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/proposals/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/proposals/save.php`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to save proposal');
  return res.json();
}

export async function apiListProposals(filters?: { customerId?: number; status?: string }) {
  const params = new URLSearchParams();
  if (filters?.customerId) params.set('customer_id', String(filters.customerId));
  if (filters?.status) params.set('status', filters.status);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${apiBase()}/proposals/list.php${qs}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load proposals');
  const data = await res.json();
  return Array.isArray(data) ? data : (data.proposals || []);
}

export async function apiUpdateProposal(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/proposals/update.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/proposals/update.php`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to update proposal');
  return res.json();
}

export async function apiDeleteProposal(id: number) {
  const token = getToken();
  const url = token
    ? `${apiBase()}/proposals/delete.php?id=${id}&_token=${encodeURIComponent(token)}`
    : `${apiBase()}/proposals/delete.php?id=${id}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to delete proposal');
  return res.json();
}

// ─── Process Trains ──────────────────────────────────────────
export async function apiListTrains(customerId?: number) {
  const qs = customerId ? `?customer_id=${customerId}` : '';
  const res = await fetch(`${apiBase()}/process-trains/list.php${qs}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load process trains');
  const data = await res.json();
  return Array.isArray(data) ? data : (data.trains || []);
}

// ─── Customer Samples (separate table) ───────────────────────
export async function apiListCustomerSamples(customerId?: number) {
  const qs = customerId ? `?customer_id=${customerId}` : '';
  const res = await fetch(`${apiBase()}/customer-samples/list.php${qs}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load customer samples');
  const data = await res.json();
  return Array.isArray(data) ? data : (data.samples || []);
}

// ─── DAF Pump Database (public endpoints) ─────────────────
export async function apiPumpManufacturers() {
  const res = await fetch(`${apiBase()}/pumps/manufacturers.php`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load manufacturers");
  return res.json();
}

export async function apiPumpSeries(manufacturerId?: string) {
  const qs = manufacturerId ? `?manufacturer_id=${encodeURIComponent(manufacturerId)}` : "";
  const res = await fetch(`${apiBase()}/pumps/series.php${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load series");
  return res.json();
}

export async function apiPumpModels(filters?: { seriesId?: string; manufacturerId?: string }) {
  const params = new URLSearchParams();
  if (filters?.seriesId) params.set("series_id", filters.seriesId);
  if (filters?.manufacturerId) params.set("manufacturer_id", filters.manufacturerId);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(`${apiBase()}/pumps/models.php${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load models");
  return res.json();
}

export async function apiPumpModel(id: string) {
  const res = await fetch(`${apiBase()}/pumps/model.php?id=${encodeURIComponent(id)}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load model");
  return res.json();
}

export async function apiPumpFormulas() {
  const res = await fetch(`${apiBase()}/pumps/formulas.php`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load formulas");
  return res.json();
}

export async function apiPumpEfficiencyMetrics() {
  const res = await fetch(`${apiBase()}/pumps/efficiency.php`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load efficiency metrics");
  return res.json();
}

// ─── Admin: Sample Assignments ───────────────────────────

/** All samples in the system — admin pool for assignment. */
export async function apiAdminSamplePool(): Promise<{ success: boolean; samples: any[] }> {
  const res = await fetch(`${apiBase()}/admin/sample-pool.php`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load sample pool');
  return res.json();
}

/** Current assignment list for a specific user. */
export async function apiAdminGetAssignments(userId: number): Promise<{ success: boolean; assignments: any[] }> {
  const res = await fetch(`${apiBase()}/admin/sample-assignments.php?user_id=${userId}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load assignments');
  return res.json();
}

/** Create (or fully reset) the Tasman Water Technologies demo account. */
export async function apiAdminCreateTasmanDemo(targetUserIds: number[] = []): Promise<{
  success: boolean;
  message: string;
  userId?: number;
  customerId?: number;
  siteIds?: number[];
  sampleIds?: number[];
  credentials?: { username: string; password: string; role: string; country: string; standard: string };
}> {
  const res = await fetch(`${apiBase()}/admin/create-tasman.php`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ target_user_ids: targetUserIds }),
  });
  if (!res.ok) throw new Error('Failed to create Tasman demo account');
  return res.json();
}

/** Replace the full assignment set for a user. */
export async function apiAdminSaveAssignments(userId: number, sampleIds: number[]): Promise<{ success: boolean; assigned: number }> {
  const res = await fetch(`${apiBase()}/admin/sample-assignments.php`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ user_id: userId, sample_ids: sampleIds }),
  });
  if (!res.ok) throw new Error('Failed to save assignments');
  return res.json();
}

// ─── Customer Sites ──────────────────────────────────────

export async function apiListCustomerSites(customerId?: number) {
  const qs = customerId ? `?customer_id=${customerId}` : '';
  const res = await fetch(`${apiBase()}/customer-sites/list.php${qs}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load customer sites');
  const data = await res.json();
  return Array.isArray(data) ? data : (data.sites || []);
}

/** Save site equipment list (bulk replace for a site+trainKey). */
export async function apiSaveSiteEquipment(payload: {
  siteId: number;
  customerId: number;
  trainKey?: string;
  trainLabel?: string;
  processTrainId?: number | null;
  equipment: Array<{
    equipmentKey: string;
    equipmentName: string;
    order: number;
    configJson?: unknown;
    outcomeJson?: unknown;
    notes?: string;
    enabled?: boolean;
  }>;
}) {
  const token = getToken();
  const body: Record<string, unknown> = token ? { ...payload, __token: token } : { ...payload };
  const url = token
    ? `${apiBase()}/site-equipment/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/site-equipment/save.php`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.text()).slice(0, 400); } catch { /* ignore */ }
    throw new Error(`site-equipment save HTTP ${res.status} ${res.statusText}${detail ? ` — ${detail}` : ''}`);
  }
  return res.json() as Promise<{ success: boolean; siteId: number; trainKey?: string; inserted: number; ids: number[] }>;
}

export async function apiListSiteEquipment(siteId: number, trainKey?: string) {
  const params = new URLSearchParams();
  params.set('site_id', String(siteId));
  if (trainKey) params.set('train_key', trainKey);
  const res = await fetch(`${apiBase()}/site-equipment/list.php?${params.toString()}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to list site equipment');
  const data = await res.json();
  return Array.isArray(data) ? data : (data.equipment || []);
}

export async function apiSaveCustomerSite(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/customer-sites/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/customer-sites/save.php`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to save site');
  return res.json();
}

// ─── EC Configurations (Electrochlorination) ────────────
export interface ECConfigSummary {
  id: number; name: string;
  cl2_kgh: string | number;
  seawater_m3h: string | number;
  modules: number;
  notes: string | null;
  created_at: string; updated_at: string;
}
export interface ECConfigFull extends ECConfigSummary { spec: any; }

export async function apiSaveECConfig(payload: { id?: number; name: string; cl2_kgh: number; seawater_m3h: number; modules: number; spec: any; notes?: string | null; }) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token ? `${apiBase()}/ec-configurations/save.php?_token=${encodeURIComponent(token)}` : `${apiBase()}/ec-configurations/save.php`;
  const res = await fetch(url, { method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }), credentials: 'include', body: JSON.stringify(body) });
  if (!res.ok) { let d=''; try { d = (await res.text()).slice(0,300); } catch{} throw new Error(`ec-config save HTTP ${res.status}${d ? ' — ' + d : ''}`); }
  return res.json() as Promise<{ success: boolean; id: number; message: string }>;
}
export async function apiListECConfigs(): Promise<ECConfigSummary[]> {
  const res = await fetch(`${apiBase()}/ec-configurations/list.php`, { headers: authHeaders(), credentials: 'include' });
  if (!res.ok) throw new Error('Failed to list EC configs');
  const d = await res.json(); return Array.isArray(d) ? d : (d.configs || []);
}
export async function apiGetECConfig(id: number): Promise<ECConfigFull> {
  const res = await fetch(`${apiBase()}/ec-configurations/list.php?id=${id}`, { headers: authHeaders(), credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load EC config');
  return (await res.json()).config;
}
export async function apiDeleteECConfig(id: number) {
  const token = getToken();
  const url = token ? `${apiBase()}/ec-configurations/delete.php?id=${id}&_token=${encodeURIComponent(token)}` : `${apiBase()}/ec-configurations/delete.php?id=${id}`;
  const res = await fetch(url, { method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }), credentials: 'include' });
  if (!res.ok) throw new Error('Failed to delete EC config');
  return res.json();
}

// ─── Lab Worksheets ─────────────────────────────────────
export interface LabWorkbookSummary {
  id: number; name: string; notes: string | null;
  sheet_count: number; cell_count: number;
  created_at: string; updated_at: string;
}
export interface LabWorkbookFull extends LabWorkbookSummary {
  sheets: any[];
}

export async function apiSaveLabWorksheet(payload: {
  id?: number; name: string; notes?: string | null; sheets: any[];
}) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/lab-worksheets/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/lab-worksheets/save.php`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.text()).slice(0, 400); } catch { /* ignore */ }
    throw new Error(`lab-worksheet save HTTP ${res.status}${detail ? ` — ${detail}` : ''}`);
  }
  return res.json() as Promise<{ success: boolean; id: number; sheetCount: number; cellCount: number; message: string }>;
}

export async function apiListLabWorksheets(): Promise<LabWorkbookSummary[]> {
  const res = await fetch(`${apiBase()}/lab-worksheets/list.php`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to list workbooks');
  const data = await res.json();
  return Array.isArray(data) ? data : (data.workbooks || []);
}

export async function apiGetLabWorksheet(id: number): Promise<LabWorkbookFull> {
  const res = await fetch(`${apiBase()}/lab-worksheets/list.php?id=${id}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load workbook');
  const data = await res.json();
  return data.workbook;
}

export async function apiDeleteLabWorksheet(id: number) {
  const token = getToken();
  const url = token
    ? `${apiBase()}/lab-worksheets/delete.php?id=${id}&_token=${encodeURIComponent(token)}`
    : `${apiBase()}/lab-worksheets/delete.php?id=${id}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to delete workbook');
  return res.json();
}

export async function apiUpdateCustomerSite(payload: Record<string, unknown>) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/customer-sites/update.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/customer-sites/update.php`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to update site');
  return res.json();
}

export async function apiDeleteCustomerSite(id: number) {
  const token = getToken();
  const url = token
    ? `${apiBase()}/customer-sites/delete.php?id=${id}&_token=${encodeURIComponent(token)}`
    : `${apiBase()}/customer-sites/delete.php?id=${id}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to delete site');
  return res.json();
}

// ─── SA Water Estimates ────────────────────────────────────────────────────

export interface SAWaterEstimatePayload {
  customer_id: number;
  site_id?: number | null;
  reference_number: string;
  sample_count: number;
  annual_kl: number;
  flow_m3h?: number | null;
  operating_days?: number | null;
  total_charge: number;
  sample_label?: string;
  notes?: string;
  estimate_data?: Record<string, unknown>;
}

export async function apiSaveSAWaterEstimate(payload: SAWaterEstimatePayload) {
  const token = getToken();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `${apiBase()}/saw-estimates/save.php?_token=${encodeURIComponent(token)}`
    : `${apiBase()}/saw-estimates/save.php`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || 'Failed to save estimate');
  }
  return res.json();
}

export async function apiListSAWaterEstimates(customerId: number, siteId?: number | null) {
  const params = new URLSearchParams({ customer_id: String(customerId) });
  if (siteId != null) params.set('site_id', String(siteId));
  const token = getToken();
  if (token) params.set('_token', token);
  const res = await fetch(`${apiBase()}/saw-estimates/list.php?${params}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || 'Failed to load estimates');
  }
  const data = await res.json();
  return Array.isArray(data) ? data : (data.estimates || []);
}
